import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Video, Mail, Phone, X, Clock, BellRing, Bell } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const BRAND_COLORS = {
  dca: '#1D4ED8',
  segcareer: '#0F766E',
  careersupport: '#0EA5E9',
  purposefit: '#10B981',
  purposetransform: '#D97706',
};

const KIND_COLORS = { Health: '#16A34A', Life: '#1D4ED8', Business: '#F97316' };
function sessionKind(b) {
  const s = `${b.session_type || ''} ${b.service_name || ''}`.toLowerCase();
  if (s.includes('health')) return 'Health';
  if (s.includes('life_jumpstart') || s.includes('life jumpstart')) return 'Life';
  if (s.includes('strategy_session') || s.includes('business jumpstart')) return 'Business';
  return null;
}
function KindTag({ b, className = '' }) {
  const k = sessionKind(b);
  if (!k) return null;
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white ${className}`} style={{ background: KIND_COLORS[k] }} data-testid={`session-tag-${b.booking_id}`}>{k}</span>;
}

function pad(n) { return String(n).padStart(2, '0'); }
function fmtDate(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${hour}:${pad(m)} ${period}`;
}

export default function AdminCalendar() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [searchParams] = useSearchParams();
  const deepBookingId = searchParams.get('booking');
  const deepDate = searchParams.get('date');
  const initialMonth = (() => {
    if (deepDate) {
      const [y, mo] = deepDate.split('-').map(Number);
      if (y && mo) return new Date(y, mo - 1, 1);
    }
    return new Date(today.getFullYear(), today.getMonth(), 1);
  })();
  const [month, setMonth] = useState(initialMonth);
  const [bookings, setBookings] = useState([]);
  const [recurringBlocks, setRecurringBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [deepHandled, setDeepHandled] = useState(false);
  const [kindFilter, setKindFilter] = useState('All');
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { setSelectedBooking(null); setSelectedDay(null); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Deep-link: open the specific booking from an SMS/email link once its month loads.
  useEffect(() => { setDeepHandled(false); }, [deepBookingId, deepDate]);
  useEffect(() => {
    if (!deepBookingId || deepHandled || bookings.length === 0) return;
    const found = bookings.find((b) => b.booking_id === deepBookingId);
    if (found) {
      setSelectedBooking(found);
      setDeepHandled(true);
    }
  }, [bookings, deepBookingId, deepHandled]);

  const resendReminder = async (bookingId) => {
    setSendingReminder(true);
    try {
      await axios.post(`${API_URL}/api/admin/scheduled-bookings/${bookingId}/send-reminder`, {}, { withCredentials: true });
      toast.success('Reminder sent to the client');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  const monthStart = useMemo(() => new Date(month.getFullYear(), month.getMonth(), 1), [month]);
  const monthEnd = useMemo(() => new Date(month.getFullYear(), month.getMonth() + 1, 0), [month]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/admin/scheduled-bookings`, {
        params: { from_date: fmtDate(monthStart), to_date: fmtDate(monthEnd) },
        withCredentials: true,
      }).then((r) => setBookings(r.data)),
      axios.get(`${API_URL}/api/recurring-blocks`).then((r) => setRecurringBlocks(r.data || [])),
    ])
      .catch(() => toast.error('Could not load calendar data'))
      .finally(() => setLoading(false));
  }, [month, monthStart, monthEnd]);

  // Group bookings by date
  const byDate = useMemo(() => {
    const m = {};
    bookings.filter((b) => kindFilter === 'All' || sessionKind(b) === kindFilter).forEach((b) => {
      if (!m[b.date]) m[b.date] = [];
      m[b.date].push(b);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.time || '').localeCompare(b.time || '')));
    return m;
  }, [bookings, kindFilter]);

  const firstDay = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();

  const totalThisMonth = Object.values(byDate).reduce((n, arr) => n + arr.length, 0);

  return (
    <AdminLayout title="Calendar">
      <div className="max-w-7xl mx-auto" data-testid="admin-calendar-page">
        <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0F1C]">Booking Calendar</h1>
            <p className="text-sm text-[#475569]">All scheduled sessions across every brand. Click any booking for details.</p>
          </div>
          <div className="flex items-center gap-3 bg-white border border-[#E2E8F0] rounded-lg px-4 py-2">
            <div className="text-xs text-[#475569]">This Month</div>
            <div className="text-2xl font-bold text-[#1D4ED8]" data-testid="calendar-month-total">{totalThisMonth}</div>
            <div className="text-xs text-[#475569]">booking{totalThisMonth === 1 ? '' : 's'}</div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="calendar-kind-filter">
          <span className="text-xs font-semibold text-[#475569] mr-1">Show:</span>
          {['All', 'Health', 'Life', 'Business'].map((k) => {
            const on = kindFilter === k;
            const c = KIND_COLORS[k] || '#0A0F1C';
            return (
              <button key={k} onClick={() => setKindFilter(k)} data-testid={`calendar-filter-${k.toLowerCase()}`}
                className="rounded-full px-4 py-1.5 text-xs font-bold border-2 transition-colors"
                style={on ? { background: c, borderColor: c, color: '#fff' } : { borderColor: c, color: c, background: '#fff' }}>
                {k === 'All' ? 'All sessions' : `${k} Jumpstart`}
              </button>
            );
          })}
        </div>

        {/* Month switcher */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#F1F5F9]">
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="p-2 hover:bg-[#F8FAFC] rounded-lg" data-testid="calendar-prev-month">
              <ChevronLeft size={18} />
            </button>
            <div className="text-center">
              <div className="text-lg font-bold text-[#0A0F1C]">{MONTH_NAMES[month.getMonth()]} {month.getFullYear()}</div>
              <button onClick={() => setMonth(new Date(today.getFullYear(), today.getMonth(), 1))} className="text-xs text-[#1D4ED8] hover:underline" data-testid="calendar-today-btn">Jump to today</button>
            </div>
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="p-2 hover:bg-[#F8FAFC] rounded-lg" data-testid="calendar-next-month">
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Brand legend */}
          <div className="px-5 py-2 border-b border-[#F1F5F9] flex flex-wrap items-center gap-4 text-[11px] text-[#475569]" data-testid="calendar-legend">
            <span className="font-bold">Brands:</span>
            {Object.entries(BRAND_COLORS).map(([slug, color]) => (
              <span key={slug} className="inline-flex items-center gap-1">
                <span className="w-3 h-3 rounded-sm" style={{ background: color }}></span>
                {slug}
              </span>
            ))}
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 border-b border-[#F1F5F9] bg-[#F8FAFC]">
            {DAY_NAMES.map((d) => (
              <div key={d} className="text-center py-2 text-xs font-bold text-[#475569] uppercase tracking-wider">{d}</div>
            ))}
          </div>

          {/* Month grid */}
          {loading ? (
            <div className="py-20 text-center"><div className="w-10 h-10 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin mx-auto"></div></div>
          ) : (
            <div className="grid grid-cols-7" data-testid="calendar-grid">
              {Array.from({ length: firstDay }, (_, i) => (
                <div key={`empty-${i}`} className="bg-[#FAFAFA] border-r border-b border-[#F1F5F9] min-h-[120px]"></div>
              ))}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const dateObj = new Date(month.getFullYear(), month.getMonth(), day);
                const dateStr = fmtDate(dateObj);
                const isToday = dateObj.getTime() === today.getTime();
                const isPast = dateObj < today;
                const dayBookings = byDate[dateStr] || [];
                const isLast = (firstDay + day) % 7 === 0;
                return (
                  <div
                    key={day}
                    className={`border-b border-[#F1F5F9] ${isLast ? '' : 'border-r'} min-h-[120px] p-2 transition-colors cursor-pointer hover:bg-[#F8FAFC] ${isToday ? 'bg-[#EFF6FF]' : 'bg-white'}`}
                    onClick={() => setSelectedDay({ date: dateStr, bookings: dayBookings, dateObj })}
                    data-testid={`calendar-day-${dateStr}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className={`text-sm font-bold ${isToday ? 'text-[#1D4ED8]' : isPast ? 'text-[#94A3B8]' : 'text-[#0A0F1C]'}`}>
                        {day}
                      </div>
                      {dayBookings.length > 0 && (
                        <div className="text-[10px] font-bold bg-[#1D4ED8] text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                          {dayBookings.length}
                        </div>
                      )}
                    </div>
                    <div className="space-y-1">
                      {/* Recurring weekly blocks for this weekday (rendered above bookings).
                          JS getDay(): Sun=0..Sat=6.  Backend day_of_week: Mon=0..Sun=6. */}
                      {recurringBlocks
                        .filter((rb) => kindFilter === 'All' && rb.day_of_week === ((dateObj.getDay() + 6) % 7))
                        .map((rb) => (
                          <div
                            key={`rb-${dateStr}-${rb.block_id}`}
                            className="w-full text-[10px] leading-tight px-1.5 py-1 rounded text-white font-medium truncate"
                            style={{ background: rb.color || '#7C3AED', border: '1px dashed rgba(255,255,255,0.5)' }}
                            title={`${rb.label} (recurring weekly)`}
                            data-testid={`calendar-recurring-${dateStr}-${rb.block_id}`}
                          >
                            <div className="font-bold truncate">{fmtTime(rb.start_time)} {rb.label}</div>
                          </div>
                        ))}
                      {dayBookings.slice(0, 3).map((b) => (
                        <button
                          key={b.booking_id}
                          onClick={(e) => { e.stopPropagation(); setSelectedBooking(b); }}
                          className="w-full text-left text-[10px] leading-tight px-1.5 py-1 rounded text-white font-medium truncate hover:opacity-90 transition-opacity"
                          style={{ background: KIND_COLORS[sessionKind(b)] || BRAND_COLORS[b.brand_slug] || '#475569' }}
                          data-testid={`calendar-booking-${b.booking_id}`}
                          title={`${b.name} · ${b.service_name || b.session_type || 'Session'} · ${fmtTime(b.time)}`}
                        >
                          <div className="font-bold truncate">{fmtTime(b.time)} {b.name}</div>
                          {sessionKind(b) && <div className="truncate text-[9px] uppercase tracking-wide opacity-90" data-testid={`grid-tag-${b.booking_id}`}>{sessionKind(b)} Jumpstart</div>}
                        </button>
                      ))}
                      {dayBookings.length > 3 && (
                        <div className="text-[10px] text-[#475569] font-medium pl-1">+ {dayBookings.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Booking detail modal */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedBooking(null)} data-testid="booking-detail-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 text-white" style={{ background: BRAND_COLORS[selectedBooking.brand_slug] || '#0A0F1C' }}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{selectedBooking.brand_name || selectedBooking.brand_slug || 'DCA Enterprises'}</div>
                  <h2 className="font-bold text-lg" data-testid="modal-booking-name">{selectedBooking.name}</h2>
                  <div className="text-xs opacity-90 flex items-center gap-2"><KindTag b={selectedBooking} className="ring-1 ring-white/60" />{selectedBooking.service_name || selectedBooking.session_type}</div>
                </div>
                <button onClick={() => setSelectedBooking(null)} className="text-white/80 hover:text-white" data-testid="modal-close-btn"><X size={18} /></button>
              </div>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm">
              <div className="flex items-center gap-2 text-[#475569]">
                <CalendarIcon size={14} className="text-[#1D4ED8]" />
                <span className="font-medium text-[#0A0F1C]">{new Date(selectedBooking.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div className="flex items-center gap-2 text-[#475569]">
                <Clock size={14} className="text-[#1D4ED8]" />
                <span className="font-medium text-[#0A0F1C]">{fmtTime(selectedBooking.time)}{selectedBooking.duration_min ? ` · ${selectedBooking.duration_min} min` : ''}</span>
              </div>
              <div className="flex items-center gap-2 text-[#475569]"><Mail size={14} /><a href={`mailto:${selectedBooking.email}`} className="text-[#1D4ED8] hover:underline">{selectedBooking.email}</a></div>
              {selectedBooking.phone && <div className="flex items-center gap-2 text-[#475569]"><Phone size={14} /><a href={`tel:${selectedBooking.phone}`} className="text-[#1D4ED8] hover:underline">{selectedBooking.phone}</a></div>}
              {/* Reminders status */}
              <div className="flex items-center gap-2 flex-wrap pt-1" data-testid="modal-reminders">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Reminders</span>
                <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${selectedBooking.reminder_24h_sent ? 'bg-green-100 text-green-700' : 'bg-[#F1F5F9] text-[#94A3B8]'}`} data-testid="reminder-badge-1">
                  {selectedBooking.reminder_24h_sent ? <BellRing size={11} /> : <Bell size={11} />} 1st {selectedBooking.reminder_24h_sent ? 'sent' : 'pending'}
                </span>
                <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${selectedBooking.reminder_1h_sent ? 'bg-green-100 text-green-700' : 'bg-[#F1F5F9] text-[#94A3B8]'}`} data-testid="reminder-badge-2">
                  {selectedBooking.reminder_1h_sent ? <BellRing size={11} /> : <Bell size={11} />} 2nd {selectedBooking.reminder_1h_sent ? 'sent' : 'pending'}
                </span>
              </div>
              <button onClick={() => resendReminder(selectedBooking.booking_id)} disabled={sendingReminder} className="inline-flex items-center justify-center gap-2 w-full border border-[#E2E8F0] text-[#0A0F1C] hover:bg-[#F8FAFC] font-semibold text-sm py-2.5 rounded-lg disabled:opacity-50" data-testid="modal-resend-reminder">
                <BellRing size={14} /> {sendingReminder ? 'Sending…' : 'Resend reminder now'}
              </button>
              {selectedBooking.zoom_join_url && (
                <a href={selectedBooking.zoom_join_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 w-full bg-[#1D4ED8] text-white font-bold py-2.5 rounded-lg hover:bg-[#2563EB]" data-testid="modal-zoom-link">
                  <Video size={14} /> Join Zoom Meeting
                </a>
              )}
              {selectedBooking.intake_responses && Object.keys(selectedBooking.intake_responses).length > 0 && (
                <div className="pt-3 border-t border-[#E2E8F0]">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#1D4ED8] mb-2">Intake Responses</div>
                  <div className="space-y-2 text-xs max-h-48 overflow-y-auto">
                    {Object.entries(selectedBooking.intake_responses).map(([k, v]) => (
                      <div key={k}>
                        <div className="font-bold text-[#0A0F1C]">{k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</div>
                        <div className="text-[#475569]">{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="pt-2 text-[10px] text-[#94A3B8] font-mono">{selectedBooking.booking_id}</div>
            </div>
          </div>
        </div>
      )}

      {/* Day-list modal (when no specific booking clicked but day was) */}
      {selectedDay && !selectedBooking && selectedDay.bookings.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedDay(null)} data-testid="day-list-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 bg-[#0A0F1C] text-white flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{selectedDay.bookings.length} booking{selectedDay.bookings.length === 1 ? '' : 's'}</div>
                <h2 className="font-bold text-base">{selectedDay.dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</h2>
              </div>
              <button onClick={() => setSelectedDay(null)} className="text-white/80 hover:text-white"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto divide-y divide-[#F1F5F9]">
              {selectedDay.bookings.map((b) => (
                <button key={b.booking_id} onClick={() => { setSelectedBooking(b); setSelectedDay(null); }} className="w-full text-left px-5 py-3 hover:bg-[#F8FAFC] flex items-center gap-3" data-testid={`day-row-${b.booking_id}`}>
                  <div className="w-1 self-stretch rounded" style={{ background: KIND_COLORS[sessionKind(b)] || BRAND_COLORS[b.brand_slug] || '#475569' }}></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm text-[#0A0F1C] truncate">{b.name}</div>
                    <div className="text-xs text-[#475569] truncate flex items-center gap-1.5"><KindTag b={b} />{b.service_name || b.session_type} · {fmtTime(b.time)}</div>
                  </div>
                  <ChevronRight size={14} className="text-[#94A3B8]" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
